import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, Pill, type Column , fmtBRL} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import type { PrefeituraContrato } from "@atlas/sdk";
import { contratoStatusInfo } from "../../lib/contrato-status";
import { produtoLabelDe } from "../../lib/produto-label";
import { PageHeader } from "./_ui";

// ISO -> "DD/MM/YYYY" (pt-BR). Null / string invalida -> "—".
function fmtDataAverbacao(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const d = new Date(t);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Contrato considera-se AVERBADO quando entrou no ciclo pos-averbacao —
 *  averbadora aplicou (Ativo/Averbado/Em dia) ou ja foi Quitado. Estados
 *  pre-averbacao (Aguardando, Cancelado, Recusado, Expirado) nao tem CCB. */
function isAverbado(situacao: string): boolean {
  const s = situacao.toLowerCase();
  return s === "ativo" || s === "averbado" || s.includes("em dia") || s.includes("em_dia") || s === "quitado";
}

export function PrefeituraContratos() {
  const q = useQuery({ queryKey: ["prefeitura", "contratos"], queryFn: () => atlas.prefeitura.contratos() });
  const [downloading, setDownloading] = useState<string | null>(null);

  async function baixarPdf(adf: string) {
    setDownloading(adf);
    try {
      const blob = await atlas.prefeitura.baixarContratoCcb(adf);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-${adf}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const err = e as Error & { status?: number; reason?: string };
      // Reason vem do backend — mensagem especifica pro operador entender.
      const msg = (() => {
        if (err.reason === "ccb_nao_anexado") return "O banco ainda nao anexou o contrato assinado deste ADF.";
        if (err.reason === "contrato_nao_e_desta_prefeitura") return "Este contrato nao pertence aos convenios desta prefeitura.";
        if (err.reason === "arquivo_nao_encontrado_no_r2") return "O contrato foi anexado, mas o arquivo nao esta mais disponivel. Peca ao banco pra reenviar.";
        if (err.reason === "contrato_nao_encontrado") return "Contrato nao encontrado.";
        if (err.reason === "r2_indisponivel") return "Armazenamento de contratos indisponivel no momento. Tente novamente em instantes.";
        return err.reason ? `Falha ao baixar o contrato (${err.reason}).` : "Falha ao baixar o contrato. Tente novamente.";
      })();
      alert(msg);
    } finally {
      setDownloading(null);
    }
  }

  const columns: Column<PrefeituraContrato>[] = [
    { key: "adf", header: "ADF", mono: true },
    { key: "nome", header: "Servidor" },
    { key: "matricula", header: "Matrícula", mono: true },
    { key: "bancoNome", header: "Banco" },
    // Tipo cru do backend (ex.: "CARTAO_BENEFICIO") -> label PT-BR via helper
    // unificado do frontend. Backend ja passa a chave canonica via
    // deriveProdutoLabel (portal-banco/store), so falta traduzir aqui.
    // Cliente 28/07/2026.
    { key: "tipoContrato", header: "Tipo", render: (c) => produtoLabelDe(c.tipoContrato) },
    { key: "valorParcela", header: "Parcela", align: "right", render: (c) => fmtBRL(c.valorParcela) },
    { key: "totalParcelas", header: "Parcelas", align: "right" },
    { key: "averbadoEm", header: "Averbado em", render: (c) => fmtDataAverbacao(c.averbadoEm) },
    { key: "situacao", header: "Situação", render: (c) => {
      // Rotulo unificado com averbadora/banco/servidor (lib/contrato-status).
      const info = contratoStatusInfo(c.situacao);
      return <Pill variant={info.variant}>{info.label}</Pill>;
    } },
    // Coluna nova (cliente 31/07/2026) — prefeitura precisa baixar o PDF do
    // contrato assinado (CCB anexado pelo banco). So aparece pra contratos
    // averbados (ativo/em_dia/quitado); nos outros mostra "—".
    { key: "adf", header: "PDF", align: "right", render: (c) => (
      isAverbado(c.situacao) ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => baixarPdf(c.adf)}
          disabled={downloading === c.adf}
        >
          {downloading === c.adf ? "..." : "↓ PDF"}
        </Button>
      ) : (
        <span style={{ color: "var(--text-dim)", fontSize: 12 }}>—</span>
      )
    ) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader title="Contratos do município" subtitle={`Total: ${q.data?.total ?? 0} · inclui ativos, em análise e cancelados`} />
      <DataTable columns={columns} rows={q.data?.contratos ?? []} rowKey={(c) => c.adf} loading={q.isLoading} emptyState="Nenhum contrato averbado." />
    </div>
  );
}
